import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area, ComposedChart, Cell, ScatterChart, Scatter, ZAxis, ReferenceArea
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  PoundSterling,
  Percent,
  BarChart3,
  RefreshCw,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  Target,
  AlertTriangle,
  Users,
  Clock
} from "lucide-react";
import { getMonthlyAnalysis, getDailyAnalysis, getCollectionTrends, getArrearsTrends, getCollectionHeatmap, getAverageDailyCollections, getFundingVsArrears } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { setCollectionsRegion } from "@/lib/regionFmt";
import { SPLIT } from "./report/reportFormat";
import TeamUtilisation from "./TeamUtilisation";
import AuTeamUtilisation from "./AuTeamUtilisation";

// Collected bar(s): UK keeps the single green bar; AU stacks Direct Debit / PayTo / online inside it (same total, same axis).
// Plain function (not a component): recharts only recognises <Bar> elements that are direct children.
function collectedBars(aus, radius = [4, 4, 0, 0]) {
  if (!aus) return <Bar key="collected" yAxisId="left" dataKey="Collected" fill="#22c55e" radius={radius} />;
  return [
    ...SPLIT.map((s) => <Bar key={s.key} yAxisId="left" dataKey={s.label} stackId="collected" fill={s.color} />),
    // sheet-era days logged as one "Cash Collected" figure (April) have no split — shown as the plain green bar on the same stack
    <Bar key="unsplit" yAxisId="left" dataKey="Collected (unsplit)" stackId="collected" fill="#22c55e" radius={radius} />,
  ];
}

export default function CollectionsAnalysis() {
  const { isAdmin } = useAuth();
  const { currentRegion } = useRegion();
  const aus = currentRegion === "AUS";
  setCollectionsRegion(currentRegion);   // module flag → api interceptor (?region=) + money formatting
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: Math.max(1, currentYear - 2024) }, (_, i) => String(2025 + i));
  const [selectedYear, setSelectedYear] = useState(String(currentYear));
  const [monthlyData, setMonthlyData] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [trends, setTrends] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [trendMonth, setTrendMonth] = useState(null);
  const [arrearsTrends, setArrearsTrends] = useState(null);
  const [heatmapData, setHeatmapData] = useState([]);
  const [arrearsViewMode, setArrearsViewMode] = useState("daily"); // daily, weekly, monthly
  const [collectionsViewMode, setCollectionsViewMode] = useState("daily"); // daily (MTD), weekly (MTD+YTD), monthly (YTD)
  const [avgDailyData, setAvgDailyData] = useState(null);
  const [avgDailyViewMode, setAvgDailyViewMode] = useState("monthly"); // monthly or weekly
  const [fundingVsArrears, setFundingVsArrears] = useState(null);
  const [fundingViewMode, setFundingViewMode] = useState("weekly"); // daily, weekly, monthly

  const loadData = async () => {
    try {
      setLoading(true);
      const [monthlyResult, dailyResult, trendsResult, arrearsResult, heatmapResult, avgDailyResult, fundingResult] = await Promise.all([
        getMonthlyAnalysis(parseInt(selectedYear)),
        getDailyAnalysis(parseInt(selectedYear), null),
        getCollectionTrends(parseInt(selectedYear)),
        getArrearsTrends(parseInt(selectedYear)).catch(() => null),
        getCollectionHeatmap(parseInt(selectedYear)).catch(() => null),
        getAverageDailyCollections(parseInt(selectedYear)).catch(() => null),
        getFundingVsArrears(parseInt(selectedYear)).catch(() => null)
      ]);
      
      setMonthlyData(monthlyResult.data || []);
      setDailyData(dailyResult.data || []);
      setTrends(trendsResult);
      setArrearsTrends(arrearsResult);
      setHeatmapData(heatmapResult?.data || []);
      setAvgDailyData(avgDailyResult);
      setFundingVsArrears(fundingResult);
    } catch (err) {
      console.error("Error loading analysis data:", err);
      toast.error("Failed to load analysis data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [selectedYear, currentRegion]); // eslint-disable-line react-hooks/exhaustive-deps

  const cur = aus ? "$" : "£";
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return `${cur}0`;
    return new Intl.NumberFormat(aus ? 'en-AU' : 'en-GB', { 
      style: 'currency', 
      currency: aus ? 'AUD' : 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatCurrencyShort = (amount) => {
    if (!amount) return `${cur}0`;
    const sign = amount < 0 ? "-" : "";
    const a = Math.abs(amount);
    if (a >= 1000000) return `${sign}${cur}${(a / 1000000).toFixed(1)}M`;
    if (a >= 1000) return `${sign}${cur}${(a / 1000).toFixed(0)}K`;
    return `${sign}${cur}${a.toFixed(0)}`;
  };

  const splitKeys = [...SPLIT.map((s) => s.label), "Collected (unsplit)"];
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const point = payload[0]?.payload || {};
      const hasSplit = aus && payload.some((e) => splitKeys.includes(e.name));
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="font-semibold text-sm mb-2">{label}{point.era === "sheet" ? <span className="ml-2 rounded bg-stone-200/70 px-1 text-[9px] font-semibold uppercase text-stone-600">sheet</span> : null}</p>
          {payload.filter((e) => !(splitKeys.includes(e.name) && !e.value)).map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.name.includes('%') ? `${entry.value?.toFixed(1)}%` : formatCurrency(entry.value)}
            </p>
          ))}
          {hasSplit && point.Collected !== undefined && <p className="text-sm font-semibold text-slate-700 border-t mt-1 pt-1">Collected: {formatCurrency(point.Collected)}</p>}
        </div>
      );
    }
    return null;
  };

  // Split keys (AU): DD / PayTo / online — summed from the daily rows so stacked bars always add up to Collected.
  const withSplit = (target, rows) => {
    if (!aus) return target;
    const sum = (k) => rows.reduce((s, d) => s + (d[k] || 0), 0);
    const dd = sum("cash_dd"), payto = sum("cash_payto"), online = sum("cash_online");
    return { ...target, "Direct Debit": dd, "PayTo": payto, "Online / other": online, "Collected (unsplit)": Math.max((target.Collected || 0) - dd - payto - online, 0) };
  };

  // Prepare chart data
  const monthlyChartData = monthlyData.map(m => ({
    month: m.month_name?.substring(0, 3),
    "Loan Due": m.loan_due,
    "Collected": m.cash_collected,
    "Collection %": m.collection_pct,
    variance: m.variance,
    era: m.sheet_days && m.sheet_days >= (m.days || 0) ? "sheet" : undefined,
    ...(aus ? { "Direct Debit": m.cash_dd || 0, "PayTo": m.cash_payto || 0, "Online / other": m.cash_online || 0,
                "Collected (unsplit)": Math.max((m.cash_collected || 0) - (m.cash_dd || 0) - (m.cash_payto || 0) - (m.cash_online || 0), 0) } : {}),
  }));

  const dailyChartData = dailyData
    .filter(d => !selectedMonth || d.month === selectedMonth)
    .slice(-60)
    .map(d => ({
      date: d.date?.split('-').slice(1).join('/'),
      "Loan Due": d.loan_due,
      "Collected": d.cash_collected,
      "Collection %": d.collection_pct
    }));

  // ── Collections Trend groupings (selectable month by day, MTD/YTD by week) ──
  const latestMonth = dailyData.length ? Math.max(...dailyData.map(d => d.month || 0)) : null;
  const trendMonths = [...new Set(dailyData.map(d => d.month).filter(Boolean))].sort((a, b) => a - b);
  const shownMonth = trendMonth ?? latestMonth;
  const mtdDaily = dailyData.filter(d => d.month === shownMonth);
  const mtdMonthName = mtdDaily[0]?.date
    ? new Date(mtdDaily[0].date).toLocaleString('en-GB', { month: 'long' })
    : '';
  const mtdDailyChart = mtdDaily.map(d => withSplit({
    date: d.date?.split('-').slice(1).reverse().join('/'),
    "Loan Due": d.loan_due,
    "Collected": d.cash_collected,
    "Collection %": d.collection_pct,
    era: d.era,
  }, [d]));
  // "AU sheet history" band — the stretch of pre-go-live days whose figures came from the team's workbook
  const sheetBand = (rows) => {
    if (!aus) return null;
    const sheetRows = rows.filter((r) => r.era === "sheet");
    if (!sheetRows.length) return null;
    return { from: sheetRows[0], to: sheetRows[sheetRows.length - 1] };
  };
  const mtdSheetBand = sheetBand(mtdDailyChart);

  const groupByWeek = (rows) => {
    const map = new Map();
    rows.forEach(d => {
      if (!d.date) return;
      const dt = new Date(d.date);
      const monday = new Date(dt);
      monday.setDate(dt.getDate() - ((dt.getDay() + 6) % 7));
      const key = monday.toISOString().split('T')[0];
      if (!map.has(key)) map.set(key, { key, due: 0, collected: 0, rows: [] });
      const w = map.get(key);
      w.due += d.loan_due || 0;
      w.collected += d.cash_collected || 0;
      w.rows.push(d);
    });
    return [...map.values()]
      .sort((a, b) => a.key.localeCompare(b.key))
      .map(w => withSplit({
        week: `w/c ${w.key.split('-').slice(1).reverse().join('/')}`,
        "Loan Due": w.due,
        "Collected": w.collected,
        "Collection %": w.due > 0 ? (w.collected / w.due) * 100 : 0,
        era: w.rows.every((r) => r.era === "sheet") ? "sheet" : undefined,
      }, w.rows));
  };
  const mtdWeeklyChart = groupByWeek(mtdDaily);
  const ytdWeeklyChart = groupByWeek(dailyData);
  const ytdSheetBand = sheetBand(ytdWeeklyChart);

  // ── Funding vs Arrears overlay ──
  const fvSeries = fundingVsArrears?.data || [];
  const weekKey = (dateStr) => {
    const dt = new Date(dateStr);
    const monday = new Date(dt);
    monday.setDate(dt.getDate() - ((dt.getDay() + 6) % 7));
    return monday.toISOString().split('T')[0];
  };
  const fvGroup = (keyFn, labelFn) => {
    const map = new Map();
    fvSeries.forEach(r => {
      if (!r.date) return;
      const k = keyFn(r.date);
      if (!map.has(k)) map.set(k, { k, funded: 0, deals: 0, arr: [], pct: [], accts: [] });
      const g = map.get(k);
      g.funded += r.funded || 0;
      g.deals += r.deals || 0;
      if (r.total_arrears != null) g.arr.push(r.total_arrears);
      if (r.arrears_pct != null) g.pct.push(r.arrears_pct);
      if (r.pct_accts_in_arrears != null) g.accts.push(r.pct_accts_in_arrears);
    });
    const avg = a => (a.length ? a.reduce((s, v) => s + v, 0) / a.length : null);
    return [...map.values()]
      .sort((a, b) => a.k.localeCompare(b.k))
      .map(g => ({
        label: labelFn(g.k),
        "Funded": Math.round(g.funded),
        "Deals": g.deals,
        "Total Arrears": avg(g.arr) != null ? Math.round(avg(g.arr)) : null,
        "Arrears %": avg(g.pct) != null ? +avg(g.pct).toFixed(2) : null,
        "% Accts in Arrears": avg(g.accts) != null ? +avg(g.accts).toFixed(2) : null,
      }));
  };
  const fvChartData =
    fundingViewMode === "daily"
      ? fvGroup(d => d, k => k.split('-').slice(1).reverse().join('/'))
      : fundingViewMode === "weekly"
        ? fvGroup(weekKey, k => `w/c ${k.split('-').slice(1).reverse().join('/')}`)
        : fvGroup(d => d.slice(0, 7), k => new Date(`${k}-01`).toLocaleString('en-GB', { month: 'short' }));

  const pearson = (pairs) => {
    const n = pairs.length;
    if (n < 4) return null;
    const mx = pairs.reduce((s, p) => s + p[0], 0) / n;
    const my = pairs.reduce((s, p) => s + p[1], 0) / n;
    let num = 0, dx = 0, dy = 0;
    pairs.forEach(([x, y]) => { num += (x - mx) * (y - my); dx += (x - mx) ** 2; dy += (y - my) ** 2; });
    return dx && dy ? num / Math.sqrt(dx * dy) : null;
  };
  const fvWeekly = fvGroup(weekKey, k => k);
  const sameWeekPairs = fvWeekly.filter(w => w["Arrears %"] != null).map(w => [w.Funded, w["Arrears %"]]);
  const lagPairs = [];
  fvWeekly.forEach((w, i) => {
    const later = fvWeekly[i + 4];
    if (later && later["Arrears %"] != null) lagPairs.push([w.Funded, later["Arrears %"]]);
  });
  const rSame = pearson(sameWeekPairs);
  const rLag = pearson(lagPairs);
  const describeR = (r) => {
    if (r == null) return "not enough data";
    const a = Math.abs(r);
    const strength = a < 0.2 ? "no real" : a < 0.4 ? "weak" : a < 0.6 ? "moderate" : a < 0.8 ? "strong" : "very strong";
    return `${strength} ${r >= 0.2 ? "positive" : r <= -0.2 ? "negative" : ""} link`.replace(/\s+/g, " ").trim();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto text-blue-600" />
          <p className="mt-4 text-muted-foreground">Loading analysis data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-foreground flex items-center gap-2" data-testid="analysis-title">
          Collections Analysis
          {aus && <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800 ring-1 ring-amber-200" data-testid="analysis-region-chip">🇦🇺 Australia · AUD</span>}
        </h1>
        <div className="flex items-center gap-3">
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-[110px] h-9">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map(y => (
                <SelectItem key={y} value={y}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Compact summary strip */}
      {trends?.summary && (
        <Card>
          <CardContent className="py-2.5 px-4">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-y-2 divide-x divide-slate-100">
              <div className="px-3 first:pl-0">
                <p className="text-[10px] uppercase font-semibold text-blue-600">Due ({selectedYear})</p>
                <p className="text-lg font-bold text-blue-700 leading-tight">{formatCurrencyShort(trends.summary.total_loan_due)}</p>
              </div>
              <div className="px-3">
                <p className="text-[10px] uppercase font-semibold text-green-600">Collected</p>
                <p className="text-lg font-bold text-green-700 leading-tight">{formatCurrencyShort(trends.summary.total_collected)}</p>
              </div>
              <div className="px-3">
                <p className={`text-[10px] uppercase font-semibold ${trends.summary.total_variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Variance</p>
                <p className={`text-lg font-bold leading-tight flex items-center gap-0.5 ${trends.summary.total_variance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {trends.summary.total_variance >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                  {formatCurrencyShort(trends.summary.total_variance)}
                </p>
              </div>
              <div className="px-3">
                <p className="text-[10px] uppercase font-semibold text-purple-600">Avg Rate</p>
                <p className="text-lg font-bold text-purple-700 leading-tight">{trends.summary.avg_collection_pct?.toFixed(1)}%</p>
              </div>
              {trends.best_month && (
                <div className="px-3">
                  <p className="text-[10px] uppercase font-semibold text-green-600 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Best Month</p>
                  <p className="text-sm font-bold text-slate-700 leading-tight mt-0.5">
                    {trends.best_month.month_name?.slice(0, 3)} <Badge className="bg-green-600 text-[10px] px-1 py-0 align-middle ml-1">{trends.best_month.collection_pct?.toFixed(1)}%</Badge>
                  </p>
                </div>
              )}
              {trends.worst_month && (
                <div className="px-3">
                  <p className="text-[10px] uppercase font-semibold text-amber-600 flex items-center gap-1"><TrendingDown className="w-3 h-3" />Lowest</p>
                  <p className="text-sm font-bold text-slate-700 leading-tight mt-0.5">
                    {trends.worst_month.month_name?.slice(0, 3)} <Badge variant="outline" className="border-amber-300 text-amber-700 text-[10px] px-1 py-0 align-middle ml-1">{trends.worst_month.collection_pct?.toFixed(1)}%</Badge>
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <Tabs defaultValue="monthly" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="monthly">Collections Trend</TabsTrigger>
          <TabsTrigger value="avgdaily">Avg Daily Collection</TabsTrigger>
          <TabsTrigger value="daily">Daily Performance</TabsTrigger>
          <TabsTrigger value="comparison">Variance Analysis</TabsTrigger>
          <TabsTrigger value="arrears">Arrears Analysis</TabsTrigger>
          <TabsTrigger value="funding" data-testid="tab-funding-vs-arrears">Funding vs Arrears</TabsTrigger>
          <TabsTrigger value="heatmap">Collection Heatmap</TabsTrigger>
          <TabsTrigger value="dow">By Day of Week</TabsTrigger>
          {isAdmin() && (
            <TabsTrigger
              value="team"
              className={aus ? "text-amber-700 font-semibold data-[state=active]:bg-amber-600 data-[state=active]:text-white" : "text-red-600 font-semibold data-[state=active]:bg-red-600 data-[state=active]:text-white"}
              data-testid="tab-team-utilisation"
            >
              Team Utilisation
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="monthly">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                {collectionsViewMode === "daily"
                  ? `${mtdMonthName} — Collections by Day${shownMonth === latestMonth ? " (MTD)" : ""}`
                  : collectionsViewMode === "weekly"
                    ? "Collections by Week"
                    : "Monthly Collection Performance (YTD)"}
              </CardTitle>
              <div className="flex items-center gap-2">
                {collectionsViewMode === "daily" && (
                  <Select value={String(shownMonth || "")} onValueChange={(v) => setTrendMonth(parseInt(v))}>
                    <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="trend-month-select">
                      <SelectValue placeholder="Month" />
                    </SelectTrigger>
                    <SelectContent>
                      {trendMonths.map((m) => (
                        <SelectItem key={m} value={String(m)}>
                          {new Date(2026, m - 1).toLocaleString('en-GB', { month: 'long' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="collections-trend-toggle">
                {['daily', 'weekly', 'monthly'].map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setCollectionsViewMode(mode)}
                    data-testid={`collections-trend-${mode}`}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      collectionsViewMode === mode
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {mode.charAt(0).toUpperCase() + mode.slice(1)}
                  </button>
                ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {collectionsViewMode === "daily" && (
                <div className="h-[400px]" data-testid="collections-mtd-daily-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={mtdDailyChart}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis yAxisId="left" tickFormatter={(v) => formatCurrencyShort(v)} tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="right" orientation="right" domain={[0, (dataMax) => Math.max(150, Math.ceil((dataMax || 0) / 50) * 50)]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      {mtdSheetBand && <ReferenceArea yAxisId="left" x1={mtdSheetBand.from.date} x2={mtdSheetBand.to.date} fill="#a8a29e" fillOpacity={0.12} label={{ value: "AU sheet history", position: "insideTop", fontSize: 10, fill: "#57534e" }} />}
                      <Bar yAxisId="left" dataKey="Loan Due" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      {collectedBars(aus)}
                      <Line yAxisId="right" type="monotone" dataKey="Collection %" stroke="#a855f7" strokeWidth={2} dot={{ r: 3 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}

              {collectionsViewMode === "weekly" && (
                <div className="space-y-8">
                  <div data-testid="collections-mtd-weekly-chart">
                    <h3 className="text-sm font-semibold text-slate-600 mb-2">{mtdMonthName} — by Week (MTD)</h3>
                    <div className="h-[280px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={mtdWeeklyChart}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                          <YAxis yAxisId="left" tickFormatter={(v) => formatCurrencyShort(v)} tick={{ fontSize: 12 }} />
                          <YAxis yAxisId="right" orientation="right" domain={[0, 150]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          <Bar yAxisId="left" dataKey="Loan Due" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                          {collectedBars(aus)}
                          <Line yAxisId="right" type="monotone" dataKey="Collection %" stroke="#a855f7" strokeWidth={2} dot={{ r: 3 }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div data-testid="collections-ytd-weekly-chart">
                    <h3 className="text-sm font-semibold text-slate-600 mb-2">Year to Date — by Week</h3>
                    <div className="h-[280px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={ytdWeeklyChart}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="week" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                          <YAxis yAxisId="left" tickFormatter={(v) => formatCurrencyShort(v)} tick={{ fontSize: 12 }} />
                          <YAxis yAxisId="right" orientation="right" domain={[0, 150]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend />
                          {ytdSheetBand && <ReferenceArea yAxisId="left" x1={ytdSheetBand.from.week} x2={ytdSheetBand.to.week} fill="#a8a29e" fillOpacity={0.12} label={{ value: "AU sheet history", position: "insideTop", fontSize: 10, fill: "#57534e" }} />}
                          <Bar yAxisId="left" dataKey="Loan Due" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                          {collectedBars(aus)}
                          <Line yAxisId="right" type="monotone" dataKey="Collection %" stroke="#a855f7" strokeWidth={2} dot={{ r: 2 }} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}

              {collectionsViewMode === "monthly" && (
                <div className="h-[400px]" data-testid="collections-ytd-monthly-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={monthlyChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                      <YAxis 
                        yAxisId="left" 
                        tickFormatter={(v) => formatCurrencyShort(v)}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        yAxisId="right" 
                        orientation="right"
                        domain={[0, 150]}
                        tickFormatter={(v) => `${v}%`}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="Loan Due" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      {collectedBars(aus)}
                      <Line yAxisId="right" type="monotone" dataKey="Collection %" stroke="#a855f7" strokeWidth={3} dot={{ r: 4 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Monthly Data Table */}
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="text-lg">Monthly Summary Table</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Month</th>
                      <th className="text-right py-3 px-4">Loan Due</th>
                      <th className="text-right py-3 px-4">Collected</th>
                      <th className="text-right py-3 px-4">Collection %</th>
                      <th className="text-right py-3 px-4">Variance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyData.map((m, idx) => (
                      <tr key={idx} className="border-b hover:bg-slate-50">
                        <td className="py-3 px-4 font-medium">{m.month_name}{aus && m.sheet_days > 0 && <span className="ml-2 rounded bg-stone-200/70 px-1 text-[9px] font-semibold uppercase tracking-wide text-stone-600" title={`${m.sheet_days} of ${m.days} days come from the team's workbook`} data-testid={`month-sheet-${m.month}`}>sheet{m.sheet_days < m.days ? " (part)" : ""}</span>}</td>
                        <td className="text-right py-3 px-4">{formatCurrency(m.loan_due)}</td>
                        <td className="text-right py-3 px-4">{formatCurrency(m.cash_collected)}</td>
                        <td className="text-right py-3 px-4">
                          <Badge variant={m.collection_pct >= 100 ? "default" : "secondary"} className={m.collection_pct >= 100 ? "bg-green-600" : ""}>
                            {m.collection_pct?.toFixed(1)}%
                          </Badge>
                        </td>
                        <td className={`text-right py-3 px-4 font-medium ${m.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(m.variance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50">
                    <tr className="font-bold">
                      <td className="py-3 px-4">Total</td>
                      <td className="text-right py-3 px-4">{formatCurrency(trends?.summary?.total_loan_due)}</td>
                      <td className="text-right py-3 px-4">{formatCurrency(trends?.summary?.total_collected)}</td>
                      <td className="text-right py-3 px-4">
                        <Badge className="bg-purple-600">{trends?.summary?.avg_collection_pct?.toFixed(1)}%</Badge>
                      </td>
                      <td className={`text-right py-3 px-4 ${trends?.summary?.total_variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(trends?.summary?.total_variance)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Average Daily Collection Trend */}
        <TabsContent value="avgdaily">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Average Daily Collection Trend
                </CardTitle>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">View by:</span>
                  <div className="flex rounded-lg border overflow-hidden">
                    <Button 
                      variant={avgDailyViewMode === "monthly" ? "default" : "ghost"} 
                      size="sm"
                      className="rounded-none"
                      onClick={() => setAvgDailyViewMode("monthly")}
                    >
                      Monthly
                    </Button>
                    <Button 
                      variant={avgDailyViewMode === "weekly" ? "default" : "ghost"} 
                      size="sm"
                      className="rounded-none"
                      onClick={() => setAvgDailyViewMode("weekly")}
                    >
                      Weekly
                    </Button>
                  </div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Total collected ÷ working days = average collected per day
              </p>
            </CardHeader>
            <CardContent>
              {avgDailyData ? (
                <>
                  {/* Summary stats */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600 uppercase font-medium">Overall Avg/Day</p>
                      <p className="text-2xl font-bold text-blue-700">
                        {formatCurrency(avgDailyData.summary?.overall_avg_daily || 0)}
                      </p>
                      <p className="text-xs text-blue-500">
                        {avgDailyData.summary?.total_working_days || 0} working days
                      </p>
                    </div>
                    {avgDailyData.best_month && (
                      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-xs text-green-600 uppercase font-medium">Best Month</p>
                        <p className="text-xl font-bold text-green-700">{avgDailyData.best_month.month_name}</p>
                        <p className="text-sm text-green-600">
                          {formatCurrency(avgDailyData.best_month.avg_daily_collected)}/day
                        </p>
                      </div>
                    )}
                    {avgDailyData.worst_month && (
                      <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-xs text-amber-600 uppercase font-medium">Lowest Month</p>
                        <p className="text-xl font-bold text-amber-700">{avgDailyData.worst_month.month_name}</p>
                        <p className="text-sm text-amber-600">
                          {formatCurrency(avgDailyData.worst_month.avg_daily_collected)}/day
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Chart */}
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart 
                        data={avgDailyViewMode === "monthly" ? avgDailyData.monthly : avgDailyData.weekly}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey={avgDailyViewMode === "monthly" ? "month_short" : "week_label"} 
                          tick={{ fontSize: 11 }}
                          interval={avgDailyViewMode === "weekly" ? 3 : 0}
                        />
                        <YAxis 
                          yAxisId="left"
                          tickFormatter={(v) => formatCurrencyShort(v)}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis 
                          yAxisId="right" 
                          orientation="right"
                          domain={[0, 150]}
                          tickFormatter={(v) => `${v}%`}
                          tick={{ fontSize: 12 }}
                        />
                        <Tooltip 
                          content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                              return (
                                <div className="bg-white p-3 border rounded-lg shadow-lg">
                                  <p className="font-semibold text-sm mb-2">{label}</p>
                                  {payload.map((entry, index) => (
                                    <p key={index} className="text-sm" style={{ color: entry.color }}>
                                      {entry.name}: {entry.name.includes('%') || entry.name.includes('Rate') 
                                        ? `${entry.value?.toFixed(1)}%` 
                                        : formatCurrency(entry.value)}
                                    </p>
                                  ))}
                                  {payload[0]?.payload?.working_days && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      {payload[0].payload.working_days} working days
                                    </p>
                                  )}
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend />
                        <Bar 
                          yAxisId="left" 
                          dataKey="avg_daily_collected" 
                          name="Avg Daily Collected" 
                          fill="#22c55e" 
                          radius={[4, 4, 0, 0]} 
                        />
                        <Line 
                          yAxisId="right" 
                          type="monotone" 
                          dataKey="collection_rate" 
                          name="Collection Rate %" 
                          stroke="#a855f7" 
                          strokeWidth={2} 
                          dot={{ r: 3 }} 
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  
                  {/* Data table */}
                  <div className="mt-6 border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium">
                            {avgDailyViewMode === "monthly" ? "Month" : "Week Starting"}
                          </th>
                          <th className="px-4 py-2 text-right font-medium">Total Collected</th>
                          <th className="px-4 py-2 text-right font-medium">Working Days</th>
                          <th className="px-4 py-2 text-right font-medium">Avg/Day</th>
                          <th className="px-4 py-2 text-right font-medium">Collection %</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {(avgDailyViewMode === "monthly" ? avgDailyData.monthly : avgDailyData.weekly)?.map((row, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-4 py-2 font-medium">
                              {avgDailyViewMode === "monthly" ? row.month_name : row.week_label}
                            </td>
                            <td className="px-4 py-2 text-right">{formatCurrency(row.total_collected)}</td>
                            <td className="px-4 py-2 text-right">{row.working_days}</td>
                            <td className="px-4 py-2 text-right font-semibold text-green-600">
                              {formatCurrency(row.avg_daily_collected)}
                            </td>
                            <td className="px-4 py-2 text-right">
                              <Badge variant={row.collection_rate >= 100 ? "default" : "outline"} 
                                className={row.collection_rate >= 100 ? "bg-green-600" : ""}>
                                {row.collection_rate?.toFixed(1)}%
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="h-[400px] flex items-center justify-center text-muted-foreground">
                  No data available for this period
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="daily">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Daily Collection Rate (Last 60 Days)
              </CardTitle>
              <Select value={selectedMonth?.toString() || "all"} onValueChange={(v) => setSelectedMonth(v === "all" ? null : parseInt(v))}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="All months" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All months</SelectItem>
                  {[...Array(12)].map((_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      {new Date(2025, i).toLocaleString('en-GB', { month: 'long' })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dailyChartData}>
                    <defs>
                      <linearGradient id="colorCollected" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis tickFormatter={(v) => formatCurrencyShort(v)} tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area type="monotone" dataKey="Collected" stroke="#22c55e" fill="url(#colorCollected)" strokeWidth={2} />
                    <Line type="monotone" dataKey="Loan Due" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="comparison">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Percent className="w-5 h-5" />
                Monthly Variance (Collected - Due)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthlyChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={(v) => formatCurrencyShort(v)} tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="variance" name="Variance" radius={[4, 4, 0, 0]}>
                      {monthlyChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.variance >= 0 ? '#22c55e' : '#ef4444'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Arrears Analysis Tab */}
        <TabsContent value="arrears">
          {arrearsTrends?.monthly_summary?.length > 0 ? (
            <div className="space-y-6">
              {/* Arrears Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {arrearsTrends.monthly_summary.map((m, idx) => (
                  <Card key={idx} className={`border-l-4 ${
                    m.arrears_pct_change > 0 ? 'border-l-red-500' : 
                    m.arrears_pct_change < 0 ? 'border-l-green-500' : 'border-l-slate-300'
                  }`}>
                    <CardContent className="pt-5">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-lg">{m.month_name}</h3>
                        {m.arrears_pct_change !== undefined && (
                          <Badge className={m.arrears_pct_change > 0 ? 'bg-red-500' : 'bg-green-500'}>
                            {m.arrears_pct_change > 0 ? '+' : ''}{m.arrears_pct_change?.toFixed(1)}%
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-muted-foreground">Avg Arrears</p>
                          <p className="font-bold text-lg">{formatCurrencyShort(m.avg_total_arrears)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Arrears %</p>
                          <p className="font-bold text-lg">{m.avg_arrears_pct?.toFixed(1)}%</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Accts in Arrears</p>
                          <p className="font-bold">{Math.round(m.avg_arrears_count)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Total Receivables</p>
                          <p className="font-bold">{formatCurrencyShort(m.avg_total_receivables)}</p>
                        </div>
                        {/* Show Principal at Risk for Nov/Dec (where data is available) */}
                        {m.avg_principal_in_arrears > 0 && (
                          <>
                            <div className="col-span-2 pt-2 border-t mt-2">
                              <p className="text-muted-foreground text-xs uppercase">Principal at Risk (Write-off Exposure)</p>
                              <p className="font-bold text-lg text-red-600">{formatCurrencyShort(m.avg_principal_in_arrears)}</p>
                            </div>
                            {m.avg_loan_book_maturity_pct > 0 && (
                              <div className="col-span-2">
                                <p className="text-muted-foreground text-xs">Loan Book Maturity: <span className="font-semibold text-foreground">{m.avg_loan_book_maturity_pct?.toFixed(1)}%</span></p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Arrears Trend Chart with View Toggle */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Arrears Trend Over Time
                  </CardTitle>
                  <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                    {['daily', 'weekly', 'monthly'].map((mode) => (
                      <button
                        key={mode}
                        onClick={() => setArrearsViewMode(mode)}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                          arrearsViewMode === mode 
                            ? 'bg-white text-slate-900 shadow-sm' 
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        {mode.charAt(0).toUpperCase() + mode.slice(1)}
                      </button>
                    ))}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="h-[400px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={
                        arrearsViewMode === 'monthly' ? arrearsTrends.monthly_summary :
                        arrearsViewMode === 'weekly' ? 
                          // Group daily data by week
                          Object.values(arrearsTrends.daily_data?.reduce((acc, d) => {
                            const date = new Date(d.date);
                            const weekStart = new Date(date);
                            weekStart.setDate(date.getDate() - date.getDay() + 1);
                            const weekKey = weekStart.toISOString().split('T')[0];
                            if (!acc[weekKey]) {
                              acc[weekKey] = { 
                                date: weekKey, 
                                week_label: `W${Math.ceil(date.getDate() / 7)} ${d.month_name?.substring(0, 3)}`,
                                total_arrears: 0, 
                                arrears_pct: 0, 
                                total_receivables: 0,
                                principal_in_arrears: 0,
                                count: 0 
                              };
                            }
                            acc[weekKey].total_arrears += d.total_arrears || 0;
                            acc[weekKey].arrears_pct += d.arrears_pct || 0;
                            acc[weekKey].total_receivables += d.total_receivables || 0;
                            acc[weekKey].principal_in_arrears += d.principal_in_arrears || 0;
                            acc[weekKey].count += 1;
                            return acc;
                          }, {}) || {}).map(w => ({
                            ...w,
                            total_arrears: w.total_arrears / w.count,
                            arrears_pct: w.arrears_pct / w.count,
                            total_receivables: w.total_receivables / w.count,
                            principal_in_arrears: w.principal_in_arrears / w.count
                          }))
                        : arrearsTrends.daily_data
                      }>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey={arrearsViewMode === 'monthly' ? 'month_name' : arrearsViewMode === 'weekly' ? 'week_label' : 'date'} 
                          tick={{ fontSize: 10 }} 
                          tickFormatter={(v) => arrearsViewMode === 'daily' ? v?.split('-').slice(1).join('/') : v?.substring(0, 3) || v} 
                        />
                        <YAxis yAxisId="left" tickFormatter={(v) => formatCurrencyShort(v)} tick={{ fontSize: 12 }} />
                        <YAxis yAxisId="right" orientation="right" domain={[0, 5]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
                        <Tooltip 
                          formatter={(value, name) => [
                            name.includes('%') || name.includes('pct') ? `${value?.toFixed(2)}%` : formatCurrency(value),
                            name === 'total_arrears' || name === 'avg_total_arrears' ? 'Total Arrears' : 
                            name === 'arrears_pct' || name === 'avg_arrears_pct' ? 'Arrears %' : name
                          ]}
                        />
                        <Legend />
                        <Area 
                          yAxisId="left" 
                          type="monotone" 
                          dataKey={arrearsViewMode === 'monthly' ? 'avg_total_arrears' : 'total_arrears'} 
                          name="Total Arrears" 
                          stroke="#f59e0b" 
                          fill="#fef3c7" 
                          strokeWidth={2} 
                        />
                        <Line 
                          yAxisId="right" 
                          type="monotone" 
                          dataKey={arrearsViewMode === 'monthly' ? 'avg_arrears_pct' : 'arrears_pct'} 
                          name="Arrears %" 
                          stroke="#ef4444" 
                          strokeWidth={2} 
                          dot={arrearsViewMode !== 'daily'} 
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Loan Book & Principal at Risk Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <PoundSterling className="w-5 h-5 text-blue-500" />
                    Loan Book & Principal at Risk
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={arrearsTrends.daily_data}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.split('-').slice(1).join('/')} />
                        <YAxis yAxisId="left" tickFormatter={(v) => formatCurrencyShort(v)} tick={{ fontSize: 12 }} />
                        <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} domain={[0, 50]} />
                        <Tooltip 
                          formatter={(value, name) => [
                            name.includes('Maturity') ? `${value?.toFixed(1)}%` : formatCurrency(value),
                            name
                          ]}
                        />
                        <Legend />
                        <Area yAxisId="left" type="monotone" dataKey="total_receivables" name="Total Loan Book" stroke="#3b82f6" fill="#dbeafe" strokeWidth={2} />
                        <Area yAxisId="left" type="monotone" dataKey="principal_in_arrears" name="Principal at Risk" stroke="#ef4444" fill="#fee2e2" strokeWidth={2} />
                        <Line yAxisId="right" type="monotone" dataKey="loan_book_maturity_pct" name="Loan Book Maturity %" stroke="#8b5cf6" strokeWidth={2} dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Total Open Loans vs Arrears % */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-500" />
                    Open Loans vs Arrears %
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={arrearsTrends.daily_data?.filter(d => d.total_open_loans > 0)}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.split('-').slice(1).join('/')} />
                        <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                        <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} domain={[0, 5]} />
                        <Tooltip 
                          formatter={(value, name) => [
                            name.includes('%') ? `${value?.toFixed(2)}%` : value,
                            name
                          ]}
                        />
                        <Legend />
                        <Bar yAxisId="left" dataKey="total_open_loans" name="Total Open Loans" fill="#6366f1" radius={[2, 2, 0, 0]} />
                        <Line yAxisId="right" type="monotone" dataKey="arrears_pct" name="Arrears %" stroke="#ef4444" strokeWidth={2} dot={false} />
                        <Line yAxisId="right" type="monotone" dataKey="pct_accts_in_arrears" name="% Accounts in Arrears" stroke="#f59e0b" strokeWidth={2} dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Arrears Ageing Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Arrears Ageing Breakdown (Monthly Average)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={arrearsTrends.monthly_summary}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="month_name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="avg_arrears_1_7" name="1-7 Days" fill="#22c55e" stackId="aging" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="avg_arrears_8_30" name="8-30 Days" fill="#f59e0b" stackId="aging" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="avg_arrears_30_plus" name="30+ Days" fill="#ef4444" stackId="aging" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No arrears data recorded for {selectedYear} yet</p>
                <p className="text-sm mt-2">Arrears metrics come from the daily collections report (available from October 2025 onwards)</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Collection Heatmap Tab */}
        {/* Funding vs Arrears Tab */}
        <TabsContent value="funding" className="space-y-6">
          {fvSeries.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center text-muted-foreground">
                <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No funding data found for {selectedYear}</p>
                <p className="text-sm mt-2">{aus ? "Funded deals come from the portal's AU Funded table (Sydney-local funded dates)" : "Funded deals come from the broker invoices Airtable (FundedDate / Amount Funded)"}</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card data-testid="fv-total-funded">
                  <CardContent className="pt-5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Total Funded ({selectedYear})</p>
                    <p className="text-2xl font-bold mt-1">{formatCurrencyShort(fundingVsArrears?.summary?.total_funded)}</p>
                    <p className="text-xs text-muted-foreground mt-1">{fundingVsArrears?.summary?.total_deals} deals · {fundingVsArrears?.summary?.days_with_funding} funding days</p>
                  </CardContent>
                </Card>
                <Card data-testid="fv-avg-week">
                  <CardContent className="pt-5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Avg Funded / Week</p>
                    <p className="text-2xl font-bold mt-1">
                      {formatCurrencyShort(fvWeekly.length ? (fundingVsArrears?.summary?.total_funded || 0) / fvWeekly.filter(w => w.Funded > 0).length : 0)}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">across {fvWeekly.filter(w => w.Funded > 0).length} active weeks</p>
                  </CardContent>
                </Card>
                <Card data-testid="fv-corr-same">
                  <CardContent className="pt-5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Funding ↔ Arrears % (same week)</p>
                    <p className="text-2xl font-bold mt-1">{rSame != null ? `r = ${rSame.toFixed(2)}` : "—"}</p>
                    <p className="text-xs text-muted-foreground mt-1 capitalize">{describeR(rSame)}</p>
                  </CardContent>
                </Card>
                <Card data-testid="fv-corr-lag">
                  <CardContent className="pt-5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Funding → Arrears % (+4 weeks)</p>
                    <p className="text-2xl font-bold mt-1">{rLag != null ? `r = ${rLag.toFixed(2)}` : "—"}</p>
                    <p className="text-xs text-muted-foreground mt-1 capitalize">{describeR(rLag)}</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Funded vs Arrears Overlay
                  </CardTitle>
                  <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="fv-toggle">
                    {['daily', 'weekly', 'monthly'].map((mode) => (
                      <button
                        key={mode}
                        onClick={() => setFundingViewMode(mode)}
                        data-testid={`fv-${mode}`}
                        className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                          fundingViewMode === mode
                            ? 'bg-white text-slate-900 shadow-sm'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        {mode.charAt(0).toUpperCase() + mode.slice(1)}
                      </button>
                    ))}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="h-[440px]" data-testid="fv-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={fvChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                        <YAxis yAxisId="left" tickFormatter={(v) => formatCurrencyShort(v)} tick={{ fontSize: 12 }} />
                        <YAxis yAxisId="right" orientation="right" domain={[0, 5]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar yAxisId="left" dataKey="Funded" fill="#6366f1" radius={[4, 4, 0, 0]} />
                        <Line yAxisId="left" type="monotone" dataKey="Total Arrears" stroke="#ef4444" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                        <Line yAxisId="right" type="monotone" dataKey="Arrears %" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    Indigo bars = amount funded ({aus ? "AU funded deals" : "broker invoices"}, deal by deal). Red line = total arrears {cur} (left axis). Amber line = arrears as % of receivables (right axis).
                    Arrears values are period averages from the daily collections report.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Percent className="w-5 h-5" />
                    Funding vs % of Accounts in Arrears
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[320px]" data-testid="fv-accts-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={fvChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                        <YAxis yAxisId="left" tickFormatter={(v) => formatCurrencyShort(v)} tick={{ fontSize: 12 }} />
                        <YAxis yAxisId="right" orientation="right" domain={[0, 40]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend />
                        <Bar yAxisId="left" dataKey="Funded" fill="#6366f1" radius={[4, 4, 0, 0]} opacity={0.7} />
                        <Line yAxisId="right" type="monotone" dataKey="% Accts in Arrears" stroke="#0ea5e9" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="heatmap">
          {heatmapData.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Collection Performance Heatmap ({selectedYear})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <div className="min-w-[700px]">
                    {/* Heatmap with Day of Week Headers — months derived from data */}
                    {[...new Map(heatmapData.map(d => [d.month, d.month_name])).entries()]
                      .sort((a, b) => a[0] - b[0])
                      .map(([monthIdx, month]) => {
                      const monthData = heatmapData.filter(d => d.month === monthIdx);
                      const monthNum = monthIdx - 1;
                      const year = parseInt(selectedYear);
                      
                      // Get first day of month (0=Sun, 1=Mon, etc)
                      const firstDayOfMonth = new Date(year, monthNum, 1).getDay();
                      // Days in month
                      const daysInMonth = new Date(year, monthNum + 1, 0).getDate();
                      // Adjust to start week on Monday (0=Mon, 6=Sun)
                      const startOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
                      
                      // Build weeks array
                      const weeks = [];
                      let currentWeek = new Array(startOffset).fill(null); // Padding for first week
                      
                      for (let day = 1; day <= daysInMonth; day++) {
                        const dayData = monthData.find(d => d.day === day);
                        currentWeek.push({ day, data: dayData });
                        
                        if (currentWeek.length === 7) {
                          weeks.push(currentWeek);
                          currentWeek = [];
                        }
                      }
                      // Add remaining days
                      if (currentWeek.length > 0) {
                        while (currentWeek.length < 7) currentWeek.push(null);
                        weeks.push(currentWeek);
                      }
                      
                      return (
                        <div key={month} className="mb-6">
                          <h3 className="font-semibold text-sm mb-2">{month} {selectedYear}</h3>
                          <div className="grid gap-1">
                            {/* Day of week header */}
                            <div className="grid grid-cols-7 gap-1 mb-1">
                              {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
                                <div key={i} className={`text-xs text-center font-medium ${i >= 5 ? 'text-muted-foreground' : ''}`}>{d}</div>
                              ))}
                            </div>
                            {/* Week rows */}
                            {weeks.map((week, weekIdx) => (
                              <div key={weekIdx} className="grid grid-cols-7 gap-1">
                                {week.map((item, dayIdx) => {
                                  if (!item) {
                                    return <div key={dayIdx} className="h-10 rounded bg-slate-50"></div>;
                                  }
                                  const collectionPct = item.data?.collection_pct || 0;
                                  const hasData = !!item.data;
                                  const bgColor = !hasData ? 'bg-slate-100' :
                                    collectionPct >= 120 ? 'bg-green-600' :
                                    collectionPct >= 100 ? 'bg-green-400' :
                                    collectionPct >= 80 ? 'bg-yellow-400' :
                                    collectionPct >= 60 ? 'bg-orange-400' :
                                    collectionPct > 0 ? 'bg-red-400' : 'bg-slate-100';
                                  const isWeekend = dayIdx >= 5;
                                  
                                  return (
                                    <div 
                                      key={dayIdx}
                                      className={`h-10 rounded ${bgColor} flex flex-col items-center justify-center cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-slate-400 transition-all ${isWeekend && !hasData ? 'opacity-50' : ''}`}
                                      title={hasData ? `${month} ${item.day}: ${collectionPct.toFixed(0)}% (${formatCurrency(item.data.cash_collected)})` : `${month} ${item.day}`}
                                    >
                                      <span className={`text-[9px] ${hasData ? 'text-white/70' : 'text-slate-400'}`}>{item.day}</span>
                                      {hasData && <span className="text-[11px] font-semibold text-white drop-shadow">{collectionPct.toFixed(0)}%</span>}
                                    </div>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Legend */}
                    <div className="flex items-center gap-4 mt-4 justify-center border-t pt-4">
                      <span className="text-xs text-muted-foreground">Collection %:</span>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded bg-red-400"></div>
                        <span className="text-xs">&lt;60%</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded bg-orange-400"></div>
                        <span className="text-xs">60-79%</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded bg-yellow-400"></div>
                        <span className="text-xs">80-99%</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded bg-green-400"></div>
                        <span className="text-xs">100-119%</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded bg-green-600"></div>
                        <span className="text-xs">≥120%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No daily collection data recorded for {selectedYear} yet (heatmap available from October 2025 onwards)</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="dow">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Average Performance by Day of Week
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trends?.daily_by_dow || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" tick={{ fontSize: 12 }} tickFormatter={(v) => v?.substring(0, 3)} />
                    <YAxis tickFormatter={(v) => formatCurrencyShort(v)} tick={{ fontSize: 12 }} />
                    <Tooltip 
                      formatter={(value, name) => [
                        name === 'avg_collection_pct' ? `${value?.toFixed(1)}%` : formatCurrency(value),
                        name === 'avg_loan_due' ? 'Avg Due' : name === 'avg_collected' ? 'Avg Collected' : 'Collection %'
                      ]}
                    />
                    <Legend formatter={(value) => value === 'avg_loan_due' ? 'Avg Due' : value === 'avg_collected' ? 'Avg Collected' : 'Collection %'} />
                    <Bar dataKey="avg_loan_due" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="avg_collected" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {isAdmin() && (
          <TabsContent value="team">
            {aus ? <AuTeamUtilisation /> : <TeamUtilisation />}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
